import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import StatusDot from '@/components/StatusDot';
import { Search, Upload, Trash2, FileText, Image, File as FileIcon, FolderOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { RecordLinkFields } from '@/components/documents/RecordLinkFields';
import { DocumentDetailSheet, DOC_CATEGORIES } from '@/components/documents/DocumentDetailSheet';
import { formatBytes, errMessage, uploadDocument } from '@/components/documents/utils';

const CATEGORIES = DOC_CATEGORIES;
const categoryIcons = { Permits: FileText, Contracts: FileText, Plans: FileText, Financial: FileText, Photos: Image, 'Internal Notes': FileText, Other: FileIcon };

export default function DocumentsPage() {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const recFilterType = searchParams.get('record_type');
  const recFilterId = searchParams.get('record_id');
  const [documents, setDocuments] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchDocs = () => {
    const params = {};
    if (category !== 'all') params.category = category;
    if (recFilterType && recFilterId) { params.record_type = recFilterType; params.record_id = recFilterId; }
    api.get('/documents', { params }).then(r => setDocuments(Array.isArray(r.data) ? r.data : []));
  };

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, recFilterType, recFilterId]);

  const openUpload = () => {
    setForm({ name: '', category: 'Other', record_type: null, record_id: null, expiration_date: '' });
    setFile(null); setProgress(0);
    setSheetOpen(true);
  };

  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
    if (f && !form.name) setForm(prev => ({ ...prev, name: f.name }));
  };

  const handleUpload = async () => {
    if (!file) return toast.error('Please choose a file to upload');
    if (form.record_type && !form.record_id) return toast.error(`Select a specific ${form.record_type.toLowerCase()}`);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', (form.name || '').trim() || file.name);
    fd.append('category', form.category || 'Other');
    if (form.record_type && form.record_id) { fd.append('record_type', form.record_type); fd.append('record_id', form.record_id); }
    if (form.expiration_date) fd.append('expiration_date', form.expiration_date);
    setUploading(true); setProgress(0);
    try {
      await uploadDocument(fd, (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); });
      toast.success('Document uploaded');
      setSheetOpen(false);
      fetchDocs();
    } catch (e) {
      toast.error(errMessage(e, 'Upload failed'));
    } finally { setUploading(false); }
  };

  const deleteDoc = async (id, e) => {
    if (e) e.stopPropagation();
    const ok = await confirm({ title: 'Delete document', description: 'Delete this document?', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    await api.delete(`/documents/${id}`);
    toast.success('Document deleted');
    fetchDocs();
  };

  const openDetail = (d) => { setSelected(d); setDetailOpen(true); };

  const catCounts = CATEGORIES.reduce((acc, c) => {
    acc[c] = documents.filter(d => d.category === c).length;
    return acc;
  }, {});

  const filtered = documents.filter(d =>
    (d.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.original_filename || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 animate-fade-in" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold text-lg tracking-tight">Documents</h1>
          <p className="text-sm text-muted-foreground">{documents.length} documents</p>
        </div>
        <Button onClick={openUpload} className="bg-primary text-primary-foreground" data-testid="upload-document-button">
          <Upload className="h-4 w-4 mr-1" /> Upload Document
        </Button>
      </div>

      {recFilterType && recFilterId && (
        <div className="flex items-center justify-between rounded-md border border-primary/30 bg-primary/10 px-3 py-2" data-testid="doc-record-filter-banner">
          <p className="text-sm" style={{ color: '#010A21' }}>Showing documents linked to <span className="font-semibold">{recFilterType}</span></p>
          <Button size="sm" variant="ghost" className="h-7 text-primary" onClick={() => setSearchParams({})} data-testid="doc-record-filter-clear">Clear filter</Button>
        </div>
      )}

      {/* Category Folders */}
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2">
        <Card
          className={`border-border shadow-none cursor-pointer transition-colors ${category === 'all' ? 'bg-primary/10 border-primary/30' : 'bg-card hover:bg-secondary/30'}`}
          onClick={() => setCategory('all')}
        >
          <CardContent className="p-3 text-center">
            <FolderOpen className="h-5 w-5 mx-auto text-primary mb-1" />
            <p className="text-xs font-semibold">All</p>
            <p className="text-xs text-muted-foreground">{documents.length}</p>
          </CardContent>
        </Card>
        {CATEGORIES.map(c => {
          const Icon = categoryIcons[c] || FileIcon;
          return (
            <Card
              key={c}
              className={`border-border shadow-none cursor-pointer transition-colors ${category === c ? 'bg-primary/10 border-primary/30' : 'bg-card hover:bg-secondary/30'}`}
              onClick={() => setCategory(c)}
            >
              <CardContent className="p-3 text-center">
                <Icon className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                <p className="text-xs font-semibold truncate">{c}</p>
                <p className="text-xs text-muted-foreground">{catCounts[c] || 0}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search documents..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" />
      </div>

      <div className="bg-transparent">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Name</TableHead>
              <TableHead className="text-xs text-muted-foreground">File</TableHead>
              <TableHead className="text-xs text-muted-foreground">Category</TableHead>
              <TableHead className="text-xs text-muted-foreground">Linked Record</TableHead>
              <TableHead className="text-xs text-muted-foreground">Upload Date</TableHead>
              <TableHead className="text-xs text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs text-muted-foreground text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(d => (
              <TableRow key={d.id} className="group border-border hover:bg-secondary/30 cursor-pointer" onClick={() => openDetail(d)} data-testid={`document-row-${d.id}`}>
                <TableCell className="font-medium text-sm" style={{ color: '#010A21' }}>{d.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground" data-testid={`document-file-${d.id}`}>
                  {d.original_filename ? (
                    <span>{d.original_filename}{d.file_size ? <span className="text-xs"> · {formatBytes(d.file_size)}</span> : null}</span>
                  ) : '\u00A0'}
                </TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{d.category}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">{d.record_type || '\u00A0'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</TableCell>
                <TableCell><StatusDot status={d.status} /></TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => deleteDoc(d.id, e)} data-testid={`delete-doc-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No documents found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Upload form */}
      <Sheet open={sheetOpen} onOpenChange={(o) => { if (!uploading) setSheetOpen(o); }}>
        <SheetContent className="sm:max-w-lg bg-card border-border overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">Upload Document</SheetTitle>
            <SheetDescription>Upload a file and link it to a record</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div>
              <Label className="text-xs text-muted-foreground">File *</Label>
              <Input type="file" onChange={onFileChange} className="bg-background border-border cursor-pointer file:text-sm file:mr-3 file:border-0 file:bg-secondary file:px-2 file:py-1 file:rounded" data-testid="document-form-file" />
              {file && <p className="text-xs text-muted-foreground mt-1" data-testid="document-form-file-info">{file.name} · {formatBytes(file.size)}</p>}
            </div>
            <div><Label className="text-xs text-muted-foreground">Document Name *</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="document-form-name" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={form.category || 'Other'} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-background border-border" data-testid="document-form-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <RecordLinkFields
              recordType={form.record_type}
              recordId={form.record_id}
              onChange={(rt, rid) => setForm(prev => ({ ...prev, record_type: rt, record_id: rid }))}
              testidPrefix="document-form"
            />

            <div><Label className="text-xs text-muted-foreground">Expiration Date</Label><Input type="date" value={form.expiration_date || ''} onChange={e => setForm({ ...form, expiration_date: e.target.value })} className="bg-background border-border" /></div>
            {uploading && (
              <div data-testid="document-form-progress">
                <div className="w-full h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} /></div>
                <p className="text-xs text-muted-foreground mt-1">Uploading... {progress}%</p>
              </div>
            )}
            <Button onClick={handleUpload} disabled={uploading} className="w-full bg-primary text-primary-foreground mt-2" data-testid="document-form-submit">
              {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading...</> : 'Upload Document'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <DocumentDetailSheet open={detailOpen} onOpenChange={setDetailOpen} doc={selected} onChanged={() => fetchDocs()} onDeleted={() => fetchDocs()} />
    </div>
  );
}
